import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { HiStar, HiCheck, HiPlus, HiChevronDown, HiMenu, HiX, HiShieldCheck, HiCreditCard, HiRefresh, HiTrendingUp, HiLightningBolt, HiCursorClick, HiChartBar, HiScale, HiUserGroup } from 'react-icons/hi'
import { trackPageView, trackCTAClick, trackSectionView, createScrollTracker } from '../utils/analytics'
import { useABTest, getHeadlineContent } from '../hooks/useABTest'
import './landing-v2.css'

// ===== LAZY SECTION WRAPPER =====
// Only renders children when section comes into view (200px before visible)
function LazySection({ children, fallback, id, className, trackName }: {
  children: React.ReactNode
  fallback?: React.ReactNode
  id?: string
  className?: string
  trackName?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [shouldRender, setShouldRender] = useState(false)
  const [tracked, setTracked] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true)
          // Track section view once when it becomes visible
          if (trackName && !tracked) {
            trackSectionView(trackName)
            setTracked(true)
          }
          observer.disconnect()
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [trackName, tracked])

  return (
    <div ref={ref} id={id} className={className}>
      {shouldRender ? children : (fallback || <SectionSkeleton />)}
    </div>
  )
}

// Skeleton placeholder for lazy-loaded sections
function SectionSkeleton() {
  return (
    <div className="section-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-line skeleton-label" />
        <div className="skeleton-line skeleton-title" />
        <div className="skeleton-line skeleton-subtitle" />
      </div>
      <div className="skeleton-content">
        <div className="skeleton-card" />
        <div className="skeleton-card" />
        <div className="skeleton-card" />
      </div>
    </div>
  )
}

// ===== DATA =====

const TICKER_ITEMS = [
  { market: 'Goals O/U 2.5', value: '1.91', change: '+2.3%' },
  { market: 'BTTS Yes', value: '1.72', change: '+1.1%' },
  { market: 'Corners O/U 10.5', value: '1.95', change: '-' },
  { market: 'Cards O/U 3.5', value: '2.10', change: '+0.8%' },
]

const FEATURES = [
  {
    icon: <HiTrendingUp />,
    title: 'Odds Tracking',
    description: 'Monitor line movements across 31 bookmakers. Get alerts when value emerges.',
  },
  {
    icon: <HiLightningBolt />,
    title: 'ML Predictions',
    description: 'Dixon-Coles model probabilities updated every 1-2 minutes during live matches.',
  },
  {
    icon: <HiCursorClick />,
    title: 'Value Finder',
    description: 'Automatic edge detection. See exactly where the bookmakers are wrong.',
  },
  {
    icon: <HiChartBar />,
    title: 'Per-90 Stats',
    description: 'Position-aware player metrics normalized for playing time.',
  },
  {
    icon: <HiScale />,
    title: 'Referee Analysis',
    description: 'Card rates, penalty frequency, and strictness scores for every official.',
  },
  {
    icon: <HiUserGroup />,
    title: 'Predicted Lineups',
    description: 'AI-generated starting XIs with confidence percentages.',
  },
]

// Testimonial structure ready for real user quotes
// To add real testimonials:
// 1. Get permission from users to use their quotes
// 2. Add optional `image` field with profile photo URL
// 3. Add optional `platform` field (e.g., "Twitter", "Trustpilot")
interface Testimonial {
  quote: string
  name: string
  role: string
  initials: string
  image?: string // Optional profile photo URL
  platform?: string // Source platform (Twitter, Trustpilot, etc.)
}

const TESTIMONIALS: Testimonial[] = [
  {
    quote: "Finally, a platform that explains the reasoning behind each bet. The transparency is incredible - I can see exactly why a line is suggested.",
    name: "Marcus T.",
    role: "Professional Bettor",
    initials: "MT",
    // image: "/testimonials/marcus.jpg", // Add when available
    // platform: "Twitter",
  },
  {
    quote: "The Dixon-Coles model and per-90 stats have completely changed how I approach player props. My hit rate has improved significantly.",
    name: "Sarah K.",
    role: "Sports Analyst",
    initials: "SK",
    // image: "/testimonials/sarah.jpg", // Add when available
    // platform: "Trustpilot",
  },
  {
    quote: "I love that it's not just tips - it's actual data. The referee analysis has saved me from making bad bets more times than I can count.",
    name: "James C.",
    role: "Weekend Bettor",
    initials: "JC",
    // image: "/testimonials/james.jpg", // Add when available
    // platform: "Product Hunt",
  },
]

const LEAGUES = [
  { name: 'Premier League', country: 'England', emoji: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { name: 'Championship', country: 'England', emoji: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { name: 'La Liga', country: 'Spain', emoji: '🇪🇸' },
  { name: 'Bundesliga', country: 'Germany', emoji: '🇩🇪' },
  { name: 'Serie A', country: 'Italy', emoji: '🇮🇹' },
  { name: 'Ligue 1', country: 'France', emoji: '🇫🇷' },
]

const BOOKMAKERS = [
  'Bet365', 'William Hill', 'Betway', 'Ladbrokes', 'Betfair',
  'Unibet', 'Pinnacle', '888sport', 'Bwin', 'Coral',
  'Paddy Power', 'Sky Bet', 'Betfred', '10Bet', 'Betsson',
  'Betvictor', 'Interwetten', 'Marathonbet', 'Sportingbet', '1xBet',
]

const PRICING_FEATURES = [
  '60+ betting markets',
  '6 major leagues',
  '31 bookmaker odds',
  'Dixon-Coles ML model',
  'Real-time live stats',
  'Predicted lineups',
  'Referee analysis',
  'Player comparisons',
]

const FAQ_ITEMS = [
  {
    question: 'How is StatScout different from tipster services?',
    answer: "We don't give tips or claim to predict winners. Instead, we provide transparent, data-driven analytics that show you exactly why a bet might be worth considering. Every suggestion includes the underlying statistics, sample sizes, and methodology.",
  },
  {
    question: 'What leagues and markets do you cover?',
    answer: "We cover 6 major leagues: Premier League, Championship, La Liga, Bundesliga, Serie A, and Ligue 1. Within each league, we analyze 60+ betting markets including match results, goals, corners, cards, player props, and more.",
  },
  {
    question: 'How often are stats updated?',
    answer: "Fixture data updates every 3-6 hours for upcoming matches. During live matches, stats refresh every 1-2 minutes including possession, shots, corners, cards, and other key metrics.",
  },
  {
    question: 'What is the Dixon-Coles model?',
    answer: "Dixon-Coles is an industry-standard statistical model for football match prediction. It accounts for home advantage, team attack/defense strengths, and correlation between low-scoring results.",
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: "Yes, absolutely. You can cancel your subscription at any time with no questions asked. If you cancel during your free trial, you won't be charged at all.",
  },
]

// ===== COMPONENTS =====

function StatsCounterV2() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const stats = [
    { end: 60, suffix: '+', label: 'Betting Markets' },
    { end: 6, suffix: '', label: 'Major Leagues' },
    { end: 31, suffix: '', label: 'Bookmakers' },
    { end: 2, suffix: ' min', label: 'Live Refresh' },
  ]

  return (
    <section ref={ref} className="stats-section-v2">
      <div className="stats-grid-v2">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className="stat-item-v2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <AnimatedNumber end={stat.end} suffix={stat.suffix} isInView={isInView} />
            <div className="stat-label-v2">{stat.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

function AnimatedNumber({ end, suffix, isInView }: { end: number; suffix: string; isInView: boolean }) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return
    const duration = 1500
    const startTime = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(easeOut * end))
      if (progress < 1) requestAnimationFrame(animate)
      else setCount(end)
    }

    requestAnimationFrame(animate)
  }, [isInView, end])

  return <div className="stat-number-v2">{count}{suffix}</div>
}

function TestimonialsSectionV2() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section ref={ref} className="testimonials-section-v2">
      <div className="section-header-v2">
        <motion.span
          className="section-label-v2"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          Testimonials
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
        >
          Loved by Bettors Worldwide
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          See what our users are saying about StatScout
        </motion.p>
      </div>

      <div className="testimonials-grid-v2">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            className="testimonial-card-v2"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
            whileHover={{ scale: 1.02, borderColor: 'var(--accent)' }}
          >
            <div className="testimonial-stars-v2">
              {[...Array(5)].map((_, i) => <HiStar key={i} />)}
            </div>
            <p className="testimonial-quote-v2">"{t.quote}"</p>
            <div className="testimonial-author-v2">
              {t.image ? (
                <img src={t.image} alt={t.name} className="testimonial-avatar-img-v2" />
              ) : (
                <div className="testimonial-avatar-v2">{t.initials}</div>
              )}
              <div>
                <span className="testimonial-name-v2">{t.name}</span>
                <span className="testimonial-role-v2">
                  {t.role}
                  {t.platform && <span className="testimonial-platform"> via {t.platform}</span>}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

function LeaguesSectionV2() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section ref={ref} className="leagues-section-v2">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
      >
        Six Major Leagues. <span className="highlight">Complete Coverage.</span>
      </motion.h2>

      <div className="leagues-grid-v2">
        {LEAGUES.map((league, i) => (
          <motion.div
            key={league.name}
            className="league-badge-v2"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4, delay: i * 0.1, type: 'spring' }}
            whileHover={{ scale: 1.05, borderColor: 'var(--accent)' }}
          >
            <span className="league-emoji">{league.emoji}</span>
            <span className="league-name">{league.name}</span>
            <span className="league-country">{league.country}</span>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

function BookmakersSectionV2() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section ref={ref} className="bookmakers-section-v2">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
      >
        Compare Odds Across <span className="highlight">31 Bookmakers</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.1 }}
      >
        Find the best prices for every market
      </motion.p>

      <div className="bookmakers-grid-v2">
        {BOOKMAKERS.map((b, i) => (
          <motion.span
            key={b}
            className="bookmaker-badge-v2"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.2 + i * 0.03 }}
          >
            {b}
          </motion.span>
        ))}
        <motion.span
          className="bookmaker-badge-v2 more"
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.3, delay: 0.2 + BOOKMAKERS.length * 0.03 }}
        >
          +11 more
        </motion.span>
      </div>
    </section>
  )
}

function PricingSectionV2({ onCTA }: { onCTA: () => void }) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [showDiscount, setShowDiscount] = useState(false)
  const [discountCode, setDiscountCode] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState<number | null>(null)
  const [discountError, setDiscountError] = useState(false)

  const basePrice = 7.95
  const discountedPrice = appliedDiscount
    ? (basePrice * (100 - appliedDiscount) / 100).toFixed(2)
    : null

  const handleApplyDiscount = () => {
    const codes: Record<string, number> = { 'LAUNCH20': 20, 'EARLY10': 10, 'FRIEND25': 25 }
    const code = discountCode.toUpperCase().trim()
    if (codes[code]) {
      setAppliedDiscount(codes[code])
      setDiscountError(false)
    } else {
      setDiscountError(true)
      setAppliedDiscount(null)
    }
  }

  return (
    <section ref={ref} className="pricing-section-v2">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
      >
        Simple, <span className="highlight">Transparent</span> Pricing
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.1 }}
      >
        Start with a 7-day free trial. Cancel anytime.
      </motion.p>

      <motion.div
        className="pricing-card-v2"
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: 0.2 }}
        whileHover={{ scale: 1.02 }}
      >
        <div className="pricing-plan-v2">PRO</div>
        <div className="pricing-price-v2">
          {appliedDiscount ? (
            <>
              <span className="price-original">£{basePrice}</span>
              £{discountedPrice}
            </>
          ) : (
            <>£{basePrice}</>
          )}
          <span className="price-period">/month</span>
        </div>
        <div className="pricing-trial-v2">
          after 7-day free trial
          {appliedDiscount && (
            <span className="discount-applied">{appliedDiscount}% off with code {discountCode.toUpperCase()}</span>
          )}
        </div>

        <ul className="pricing-features-v2">
          {PRICING_FEATURES.map((f, i) => (
            <motion.li
              key={f}
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
            >
              <HiCheck className="check-icon" />
              {f}
            </motion.li>
          ))}
        </ul>

        <button className="btn btn-primary btn-large" onClick={onCTA}>
          Start Free Trial
        </button>

        <p className="pricing-guarantee-v2">Card required to start trial. Cancel anytime.</p>

        <div className="discount-section-v2">
          <button className="discount-toggle-v2" onClick={() => setShowDiscount(!showDiscount)}>
            Have a discount code?
            <HiChevronDown style={{ transform: showDiscount ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>

          <AnimatePresence>
            {showDiscount && (
              <motion.div
                className="discount-form-v2"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <input
                  type="text"
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value); setDiscountError(false) }}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                />
                <button onClick={handleApplyDiscount}>Apply</button>
              </motion.div>
            )}
          </AnimatePresence>

          {discountError && <p className="discount-error">Invalid discount code</p>}
          {appliedDiscount && <p className="discount-success"><HiCheck /> Discount applied!</p>}
        </div>
      </motion.div>
    </section>
  )
}

function FAQSectionV2() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section ref={ref} className="faq-section-v2">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
      >
        Frequently Asked <span className="highlight">Questions</span>
      </motion.h2>

      <div className="faq-list-v2">
        {FAQ_ITEMS.map((item, i) => (
          <motion.div
            key={i}
            className={`faq-item-v2 ${openIndex === i ? 'open' : ''}`}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: i * 0.1 }}
          >
            <button className="faq-question-v2" onClick={() => setOpenIndex(openIndex === i ? null : i)}>
              <span>{item.question}</span>
              <HiPlus className="faq-icon-v2" />
            </button>
            <div className="faq-answer-v2">
              <p>{item.answer}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

// ===== VIDEO DEMO COMPONENT =====
// Placeholder component - enable once video is created
// To enable: set VIDEO_DEMO_ENABLED to true and update VIDEO_ID
const VIDEO_DEMO_ENABLED = false
const VIDEO_ID = 'YOUR_VIDEO_ID' // Replace with YouTube video ID

function VideoDemo() {
  const [playing, setPlaying] = useState(false)

  if (!VIDEO_DEMO_ENABLED) {
    return null
  }

  if (!playing) {
    return (
      <div
        className="video-demo-thumbnail"
        onClick={() => setPlaying(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setPlaying(true)}
      >
        {/* Placeholder thumbnail - replace with actual screenshot */}
        <div className="video-thumbnail-placeholder">
          <div className="play-button">
            <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <span className="video-label">Watch 2-min Demo</span>
        </div>
      </div>
    )
  }

  return (
    <div className="video-demo-player">
      <iframe
        src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
        title="StatScout Demo"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}

// ===== APP SCREENSHOT COMPONENT =====

function AppScreenshot() {
  const [imageError, setImageError] = useState(false)

  if (imageError) {
    // Fallback placeholder if image doesn't exist
    return (
      <div className="app-screenshot-placeholder">
        <div className="placeholder-header">
          <span className="placeholder-title">Premier League - Today</span>
          <span className="placeholder-live"><span className="live-dot" />LIVE</span>
        </div>
        <div className="placeholder-content">
          <div className="placeholder-odds">
            <div className="placeholder-odd"><span>Home</span><strong>1.85</strong></div>
            <div className="placeholder-odd"><span>Draw</span><strong>3.40</strong></div>
            <div className="placeholder-odd highlight"><span>Away</span><strong>4.20</strong></div>
          </div>
          <div className="placeholder-match">
            <div className="placeholder-team">Arsenal</div>
            <div className="placeholder-vs">vs</div>
            <div className="placeholder-team">Chelsea</div>
          </div>
          <div className="placeholder-markets">
            <span>60+ Markets</span>
            <span>31 Bookmakers</span>
            <span>Live Updates</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <picture>
      <source srcSet="/app-screenshot.webp" type="image/webp" />
      <img
        src="/app-screenshot.png"
        alt="StatScout app showing fixture analysis with odds comparison"
        className="app-screenshot"
        loading="eager"
        fetchPriority="high"
        onError={() => setImageError(true)}
      />
    </picture>
  )
}

// ===== HOW IT WORKS DATA =====

const HOW_IT_WORKS_STEPS = [
  {
    step: '01',
    title: 'Pick a Fixture',
    description: 'Browse upcoming matches across 6 major leagues. Filter by date, competition, or teams.',
  },
  {
    step: '02',
    title: 'Explore the Builder',
    description: 'Dive into 60+ markets with per-90 stats, referee data, and H2H insights for each selection.',
  },
  {
    step: '03',
    title: 'Find Value',
    description: 'Our edge badges highlight where bookmaker odds diverge from model probabilities.',
  },
  {
    step: '04',
    title: 'Build Your Bet',
    description: 'Combine selections with correlation warnings. Track your bets and monitor CLV.',
  },
]

// ===== COMPARISON TABLE DATA =====

const COMPARISON_FEATURES = [
  { feature: 'Transparent methodology', statscout: true, tipsters: false, spreadsheets: false },
  { feature: 'Dixon-Coles ML model', statscout: true, tipsters: false, spreadsheets: false },
  { feature: 'Per-90 player stats', statscout: true, tipsters: false, spreadsheets: true },
  { feature: 'Real-time odds from 31 bookmakers', statscout: true, tipsters: false, spreadsheets: false },
  { feature: 'Referee card analysis', statscout: true, tipsters: false, spreadsheets: false },
  { feature: 'Automatic value detection', statscout: true, tipsters: false, spreadsheets: false },
  { feature: 'No conflict of interest', statscout: true, tipsters: false, spreadsheets: true },
  { feature: 'Live in-play updates', statscout: true, tipsters: true, spreadsheets: false },
]

// ===== MAIN COMPONENT =====

// Hook to track when a section becomes visible
function useSectionTracking(sectionName: string) {
  const ref = useRef<HTMLElement>(null)
  const tracked = useRef(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !tracked.current) {
          trackSectionView(sectionName)
          tracked.current = true
          observer.disconnect()
        }
      },
      { threshold: 0.3 } // 30% of section must be visible
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [sectionName])

  return ref
}

export default function LandingPageV2() {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // A/B test for hero headline
  const headlineVariant = useABTest('hero_headline')
  const headline = getHeadlineContent(headlineVariant)

  // Section tracking refs
  const featuresRef = useSectionTracking('features')
  const howItWorksRef = useSectionTracking('how_it_works')
  const comparisonRef = useSectionTracking('comparison')

  // Track page view and scroll depth on mount
  useEffect(() => {
    trackPageView('/landing')
    const cleanup = createScrollTracker()()
    return cleanup
  }, [])

  const handleStartTrial = (location: string) => {
    trackCTAClick(location, headlineVariant)
    navigate('/onboarding')
  }

  const handleSignIn = () => {
    trackCTAClick('nav', headlineVariant)
    navigate('/sign-in')
  }

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileMenuOpen])

  return (
    <div className="landing-v2">
      {/* Navigation */}
      <nav className="nav-v2-landing">
        <div className="nav-inner">
          <a href="#" className="logo">
            <span className="logo-dot" />
            StatScout
          </a>
          <div className="nav-links">
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features') }}>Features</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works') }}>How It Works</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing') }}>Pricing</a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }}>FAQ</a>
          </div>
          <div className="nav-buttons">
            <button className="btn btn-outline" onClick={handleSignIn}>
              Sign In
            </button>
            <button className="btn btn-primary" onClick={() => handleStartTrial('nav')}>
              Start Free Trial
            </button>
          </div>
          {/* Mobile hamburger button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <motion.div
              className="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mobile-menu-links">
                <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features') }}>Features</a>
                <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works') }}>How It Works</a>
                <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing') }}>Pricing</a>
                <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }}>FAQ</a>
              </div>
              <div className="mobile-menu-buttons">
                <button className="btn btn-outline" onClick={() => { setMobileMenuOpen(false); handleSignIn() }}>
                  Sign In
                </button>
                <button className="btn btn-primary" onClick={() => { setMobileMenuOpen(false); handleStartTrial('mobile_nav') }}>
                  Start Free Trial
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="hero-v2">
        <div className="hero-content">
          {/* Social proof badge */}
          <div className="hero-social-proof">
            <div className="social-proof-avatars">
              <span className="avatar">JT</span>
              <span className="avatar">MK</span>
              <span className="avatar">SC</span>
              <span className="avatar">+</span>
            </div>
            <span className="social-proof-text">
              <strong>500+</strong> bettors using StatScout
            </span>
          </div>

          <h1>
            {headline.line1}<br />
            <span className="highlight">{headline.line2}</span>
          </h1>
          <p>
            The analytics platform that shows you <strong>why</strong>, not just what to bet.
            Data-driven insights across 60+ markets, 6 leagues, and 31 bookmakers.
          </p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => handleStartTrial('hero')}>
              Start 7-Day Free Trial
            </button>
            <button className="btn btn-outline" onClick={() => scrollToSection('how-it-works')}>
              See How It Works
            </button>
          </div>

          {/* Trust badges */}
          <div className="trust-badges">
            <div className="trust-badge">
              <HiShieldCheck />
              <span>Secure payments</span>
            </div>
            <div className="trust-badge">
              <HiCreditCard />
              <span>Cancel anytime</span>
            </div>
            <div className="trust-badge">
              <HiRefresh />
              <span>No lock-in</span>
            </div>
          </div>
        </div>

        {/* App Screenshot */}
        <div className="app-screenshot-wrapper">
          <div className="app-screenshot-frame">
            <div className="screenshot-browser-bar">
              <span className="browser-dot red" />
              <span className="browser-dot yellow" />
              <span className="browser-dot green" />
            </div>
            <AppScreenshot />
          </div>
        </div>
      </section>

      {/* Ticker */}
      <div className="ticker-v2-landing">
        <div className="ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, index) => (
            <div key={`${item.market}-${index}`} className="ticker-item">
              <span className="ticker-name">{item.market}</span>
              <span className="ticker-value">{item.value}</span>
              <span className={`ticker-change ${item.change !== '-' ? 'up' : ''}`}>
                {item.change}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Counter */}
      <StatsCounterV2 />

      {/* Video Demo - uncomment when video is ready and VIDEO_DEMO_ENABLED is true */}
      {VIDEO_DEMO_ENABLED && (
        <section className="video-demo-section">
          <div className="section-header-v2">
            <span className="section-label-v2">See It In Action</span>
            <h2>2-Minute <span className="highlight">Demo</span></h2>
            <p>Watch how StatScout helps you find value bets</p>
          </div>
          <div className="video-demo-container">
            <VideoDemo />
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="features-v2" ref={featuresRef}>
        <div className="features-header">
          <h2>Built for <span className="highlight">Serious Bettors</span></h2>
          <p>Professional-grade tools at your fingertips</p>
        </div>

        <div className="features-grid">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section-v2" ref={howItWorksRef}>
        <div className="section-header-v2">
          <motion.span
            className="section-label-v2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            How It Works
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            From Fixture to <span className="highlight">Value Bet</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Four simple steps to smarter betting decisions
          </motion.p>
        </div>

        <div className="how-it-works-grid">
          {HOW_IT_WORKS_STEPS.map((step, index) => (
            <motion.div
              key={step.step}
              className="how-it-works-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <span className="step-number">{step.step}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="comparison-section-v2" ref={comparisonRef}>
        <div className="section-header-v2">
          <motion.span
            className="section-label-v2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why StatScout?
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Data, Not <span className="highlight">Guesswork</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            See how we compare to traditional approaches
          </motion.p>
        </div>

        <motion.div
          className="comparison-table-wrapper"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th className="highlight-col">StatScout</th>
                <th>Tipster Services</th>
                <th>DIY Spreadsheets</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_FEATURES.map((row, i) => (
                <tr key={i}>
                  <td>{row.feature}</td>
                  <td className="highlight-col">
                    {row.statscout ? <HiCheck className="check" /> : <HiX className="cross" />}
                  </td>
                  <td>
                    {row.tipsters ? <HiCheck className="check" /> : <HiX className="cross" />}
                  </td>
                  <td>
                    {row.spreadsheets ? <HiCheck className="check" /> : <HiX className="cross" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </section>

      {/* Testimonials - Lazy loaded */}
      <LazySection trackName="testimonials">
        <TestimonialsSectionV2 />
      </LazySection>

      {/* Leagues - Lazy loaded */}
      <LazySection trackName="leagues">
        <LeaguesSectionV2 />
      </LazySection>

      {/* Bookmakers - Lazy loaded */}
      <LazySection trackName="bookmakers">
        <BookmakersSectionV2 />
      </LazySection>

      {/* Pricing - Lazy loaded */}
      <LazySection id="pricing" trackName="pricing">
        <PricingSectionV2 onCTA={() => handleStartTrial('pricing')} />
      </LazySection>

      {/* FAQ - Lazy loaded */}
      <LazySection id="faq" trackName="faq">
        <FAQSectionV2 />
      </LazySection>

      {/* Final CTA */}
      <section className="cta-section-v2">
        <h2>Start <span className="highlight">Winning</span> Today</h2>
        <p>7-day free trial. Then just £7.95/month. Cancel anytime.</p>
        <button className="btn btn-primary" onClick={() => handleStartTrial('footer_cta')}>
          Start Free Trial
        </button>
      </section>

      {/* Footer */}
      <footer className="footer-v2">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="logo-dot" />
            <span>StatScout</span>
          </div>
          <div className="footer-links">
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features') }}>Features</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works') }}>How It Works</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing') }}>Pricing</a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }}>FAQ</a>
          </div>
          <div className="footer-legal">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} StatScout — Bet smarter, not harder.</p>
          <p className="footer-disclaimer">18+ only. Please gamble responsibly. <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer">BeGambleAware.org</a></p>
        </div>
      </footer>
    </div>
  )
}
